/**
 * The Read Aloud control (`SpeakResponseButton`): starts or stops speech for
 * one finished assistant message.
 *
 * The heavy lifting - download, playback, speaker coordination with Voice
 * Mode - lives elsewhere, in `ReadAloudStore`.
 */
import { captureException } from '@sentry/react';
import { observer } from 'mobx-react-lite';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { IconButton } from '@/components/ui';
import { readAloudStore } from '@/features/chat/voice/read-aloud.store';
import { trackEvent } from '@/utils/analytics';

interface SpeakResponseButtonProps {
  text: string;
  threadId: string;
  messageId: string;
  messageIndex: number;
}

export const SpeakResponseButton = observer(function SpeakResponseButton({
  text,
  threadId,
  messageId,
  messageIndex,
}: SpeakResponseButtonProps) {
  const readAloudId = `${threadId}:${messageId}`;
  const isThisMessagePlaying =
    readAloudStore.currentReadAloudId === readAloudId;

  const handleSpeak = async () => {
    if (isThisMessagePlaying) {
      readAloudStore.stopCurrentRequest();
      return;
    }

    trackEvent('MESSAGE_LB_SPEAK', {
      chatId: threadId,
      messageIndex,
    });
    try {
      await readAloudStore.play(text, readAloudId, threadId);
    } catch (error) {
      captureException(error);
      toast.error("Couldn't read response aloud");
    }
  };

  return (
    <IconButton
      tooltip={isThisMessagePlaying ? 'Stop' : 'Read aloud'}
      size="xs"
      icon={isThisMessagePlaying ? Icons.stop : Icons.audio}
      onClick={handleSpeak}
      aria-label={
        isThisMessagePlaying
          ? 'Stop reading response aloud'
          : 'Read response aloud'
      }
      data-testid="speak-response-button"
      data-read-aloud-playing={isThisMessagePlaying || undefined}
    />
  );
});
